import { Suspense, lazy, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { AppShell } from '@/shared/components/layout/AppShell';
import { ForcePasswordChangeGate } from '@/features/auth/components/ForcePasswordChangeGate';
import { env } from '@/shared/lib/env';
import { LEGACY_REDIRECTS, routeLoaders } from '@/app/routes/registry';

// Lazy components resolve their chunks via the central registry so the
// sidebar's `prefetchRoute()` and the router's `lazy()` share the exact
// same loader — no double-fetch on first navigation. See routes/registry.ts.
const LoginPage             = lazy(routeLoaders['/login']);
const HomePage              = lazy(routeLoaders['/']);
const CmsDashboardPage      = lazy(routeLoaders['/cms']);
const CareersDashboardPage  = lazy(routeLoaders['/careers']);
const CrmDashboardPage      = lazy(routeLoaders['/crm']);
const AdministrationDashboardPage = lazy(routeLoaders['/administration']);
const PostsListPage         = lazy(routeLoaders['/cms/posts']);
const PostNewPage           = lazy(routeLoaders['/cms/posts/new']);
const PostEditPage          = lazy(routeLoaders['/cms/posts/:id/edit']);
const CategoriesPage        = lazy(routeLoaders['/cms/categories']);
const TagsPage              = lazy(routeLoaders['/cms/tags']);
const MediaPage             = lazy(routeLoaders['/cms/media']);
const InsightsPage          = lazy(routeLoaders['/cms/insights']);
const PopupsListPage        = lazy(routeLoaders['/cms/popups']);
const TeamListPage          = lazy(routeLoaders['/cms/team']);
const SeoPage               = lazy(routeLoaders['/cms/seo']);
const JobsListPage          = lazy(routeLoaders['/careers/jobs']);
const JobNewPage            = lazy(routeLoaders['/careers/jobs/new']);
const JobEditPage           = lazy(routeLoaders['/careers/jobs/:id/edit']);
const ApplicationsInboxPage = lazy(routeLoaders['/careers/applications']);
const LeadsListPage         = lazy(routeLoaders['/crm/leads']);
const LeadDetailPage        = lazy(routeLoaders['/crm/leads/:id']);
const UsersPage             = lazy(routeLoaders['/administration/users']);
const UserDetailPage        = lazy(routeLoaders['/administration/users/:id']);
const TeamsListPage         = lazy(routeLoaders['/administration/teams']);
const TeamDetailPage        = lazy(routeLoaders['/administration/teams/:id']);
const RolesListPage         = lazy(routeLoaders['/administration/roles']);
const RoleEditPage          = lazy(routeLoaders['/administration/roles/:id']);
const AccessControlPage     = lazy(routeLoaders['/administration/access-control']);
const AuditLogPage          = lazy(routeLoaders['/administration/audit']);
const SettingsPage          = lazy(routeLoaders['/administration/settings']);

function FullPageLoader() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
      <CircularProgress size={32} />
    </Box>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const location = useLocation();
  if (isHydrating) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

/**
 * Catches a stale URL (e.g. `/posts/<id>/edit`) and bounces to the new
 * module-scoped equivalent (`/cms/posts/<id>/edit`). Preserves the rest
 * of the path AND the query string so deep-links keep working.
 */
function LegacySubpathRedirect({ from, to }: { from: string; to: string }) {
  const location = useLocation();
  const tail = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : '';
  return <Navigate to={`${to}${tail}${location.search}`} replace />;
}

export function App() {
  const setSession = useAuthStore((s) => s.setSession);
  const setHydrating = useAuthStore((s) => s.setHydrating);
  const tried = useRef(false);

  useEffect(() => {
    if (tried.current) return;
    tried.current = true;
    (async () => {
      try {
        const res = await axios.post(
          `${env.VITE_API_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true, timeout: 10_000 },
        );
        setSession(res.data);
      } catch {
        useAuthStore.getState().clearSession();
      } finally {
        setHydrating(false);
      }
    })();
  }, [setSession, setHydrating]);

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ForcePasswordChangeGate>
                <AppShell />
              </ForcePasswordChangeGate>
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />

          {/* ── Module dashboards ─────────────────────────────────── */}
          <Route path="cms" element={<CmsDashboardPage />} />
          <Route path="careers" element={<CareersDashboardPage />} />
          <Route path="crm" element={<CrmDashboardPage />} />
          <Route path="administration" element={<AdministrationDashboardPage />} />

          {/* ── CMS ────────────────────────────────────────────────── */}
          <Route path="cms/posts" element={<PostsListPage />} />
          <Route path="cms/posts/new" element={<PostNewPage />} />
          <Route path="cms/posts/:id/edit" element={<PostEditPage />} />
          <Route path="cms/categories" element={<CategoriesPage />} />
          <Route path="cms/tags" element={<TagsPage />} />
          <Route path="cms/media" element={<MediaPage />} />
          <Route path="cms/insights" element={<InsightsPage />} />
          <Route path="cms/popups" element={<PopupsListPage />} />
          <Route path="cms/team" element={<TeamListPage />} />
          <Route path="cms/seo" element={<SeoPage />} />

          {/* ── Careers ───────────────────────────────────────────── */}
          <Route path="careers/jobs" element={<JobsListPage />} />
          <Route path="careers/jobs/new" element={<JobNewPage />} />
          <Route path="careers/jobs/:id/edit" element={<JobEditPage />} />
          <Route path="careers/applications" element={<ApplicationsInboxPage />} />

          {/* ── CRM ───────────────────────────────────────────────── */}
          <Route path="crm/leads" element={<LeadsListPage />} />
          <Route path="crm/leads/:id" element={<LeadDetailPage />} />
          {/* Newsletter view reuses the same list page filtered to FormType=Newsletter.
              Detail rows still resolve via /crm/leads/:id so we keep a single drawer. */}
          <Route path="crm/newsletter" element={<LeadsListPage formType="Newsletter" />} />
          <Route path="crm/newsletter/:id" element={<LeadDetailPage />} />

          {/* ── Administration ────────────────────────────────────── */}
          <Route path="administration/users" element={<UsersPage />} />
          <Route path="administration/users/:id" element={<UserDetailPage />} />
          <Route path="administration/teams" element={<TeamsListPage />} />
          <Route path="administration/teams/:id" element={<TeamDetailPage />} />
          <Route path="administration/roles" element={<RolesListPage />} />
          <Route path="administration/roles/:id" element={<RoleEditPage />} />
          <Route path="administration/access-control" element={<AccessControlPage />} />
          <Route path="administration/audit" element={<AuditLogPage />} />
          <Route path="administration/settings" element={<SettingsPage />} />

          {/* ── Legacy redirects ──────────────────────────────────── */}
          {/* Bookmarks, audit-log entries, and email links that still
              point at the pre-restructure URLs land here and bounce to
              the new module-scoped path. Sub-paths preserve via /* glob. */}
          {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from.slice(1)} element={<Navigate to={to} replace />} />
          ))}
          {/* Sub-path redirects — keep nested children (e.g. /posts/:id/edit). */}
          <Route path="posts/*" element={<LegacySubpathRedirect from="/posts" to="/cms/posts" />} />
          <Route path="users/*" element={<LegacySubpathRedirect from="/users" to="/administration/users" />} />
          <Route path="teams/*" element={<LegacySubpathRedirect from="/teams" to="/administration/teams" />} />
          <Route path="roles/*" element={<LegacySubpathRedirect from="/roles" to="/administration/roles" />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
